<template>
  <b-modal
    v-model="importModal.show"
    :title="title"
    hide-footer
    @hidden="onHideModal"
    @show="onShowModal"
  >
    <content-spinner :show="loading" />
    <div v-if="!loading">
      <div v-if="isImportTypeFile">
        <p
          v-if="importedFileErrors"
          class="alert alert-danger p-2"
        >
          {{ importedFileErrors }}
        </p>
        <b-form-file
          id="importFileInput"
          no-drop
          placeholder="Choose a file"
          @change="uploadXlsx"
        />
      </div>
      <div v-else>
        <p
          v-if="pastedDataError"
          class="alert alert-danger p-2"
        >
          {{ pastedDataError }}
        </p>
        <div
          v-if="!tableTextData"
          class="paste-data-text"
        >
          <h5>Paste data</h5>
        </div>
        <b-form-textarea
          id="importFileInput"
          ref="textArea"
          v-model="tableTextData"
          @input="parseFile(tableTextData)"
          @change="onChange"
        />
      </div>
    </div>
  </b-modal>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import readXlsxFile from 'read-excel-file';
import ContentSpinner from '../ContentSpinner';
import Papa from 'papaparse';
import fieldMixin from '../../mixins/field.mixin';

const {mapState, mapActions, mapGetters} = createNamespacedHelpers('dataManagement');
export default {
  name: 'ImportModal',
  components: {ContentSpinner},
  mixins: [fieldMixin],
  props: {
    type: {
      type: String,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      tableTextData: '',
      pastedDataError: '',
      importedFileErrors: '',
      isFile: null,
      headers: [],
      loading: false,
    };
  },
  computed: {
    ...mapState({
      importModal: state => state.table.importModal,
      tableFields: state => state.table.schema.fields,
    }),
    isImportTypeFile() {
      return this.type === 'import-file';
    },
    isUploadData() {
      return this.type === 'upload-data';
    },
    title() {
      return this.isImportTypeFile ? 'Import File' : 'Upload Data';
    },
  },
  methods: {
    ...mapActions(['hideImportModal', 'showImportTableViewModal']),
    onHideModal() {
      this.isFile = null;
      this.tableTextData = '';
      this.pastedDataError = '';
      this.importedFileErrors = '';
      this.hideImportModal();
    },
    onShowModal() {
      if (this.isUploadData) {
        setTimeout(() => this.$refs.textArea.focus());
      }
    },
    //todo: do not delete (Roman Poplietukhin)
    // uploadCSV(e) {
    //   let file = e.target.files[0]
    //   let vm = this
    //   if (file.name.slice(file.name.length - 4).toLowerCase() !== '.csv') {
    //     vm.isFile = false;
    //     this.importedFileErrors = 'File should be CSV';
    //   } else if (!window.FileReader) {
    //     vm.$toasted.error(`Browser not supported`, {duration: TOASTER_DURATION})
    //   } else {
    //     vm.isFile = true;
    //     this.importedFileErrors = '';
    //     let reader = new FileReader();
    //
    //     reader.readAsText(e.target.files[0]);
    //     reader.onload = event => {
    //       let csv = event.target.result;
    //       vm.parseFile(csv, true);
    //     };
    //     reader.onerror = event => {
    //       if (event.target.error.name === 'NotReadableError') {
    //         vm.$toasted.error(`Error occurred`, {duration: TOASTER_DURATION})
    //       }
    //     }
    //   }
    // },
    parseFile(rows, isFile = false) {
      this.loading = true;
      let lines = rows;
      if(!isFile){
        // Parse copied data into CSV
        lines = Papa.parse(rows, { delimiter: '\t', skipEmptyLines: true }).data;

        // Check if it parsed into a single column (indicating tab delimiter didn't work)
        const hasSingleColumn = lines[0] && lines[0].length === 1;

        // If it seems to be single column, try parsing with comma as delimiter
        if (hasSingleColumn) {
          lines = Papa.parse(rows, { delimiter: ',', skipEmptyLines: true }).data;
        }
      }
      const result = [];
      const tableFields = this.tableFields.map(f => f.name);
      tableFields.push('Record Status');
      this.headers = lines[0]
        .map(l => {
          const field =  this.tableFields.find(f => (f.isPrimaryKey ? f.name : f.displayName).trim() === l);
          return field ? field.name : l;
        })
        .filter(l => tableFields.includes(l));

      if (this.isValidColumns().length > 0 && this.isValidColumns().length !== this.fields.length) {
        this.loading = false;
        if (this.isFile) {
          this.importedFileErrors = `'${this.isValidColumns().map(c => c).join(', ')}' column(s) is missing from the file`;
        } else {
          this.pastedDataError = `'${this.isValidColumns().map(c => c).join(', ')}' column(s) is missing from the file`;
        }
        return;
      }

      if (!this.isValidHeaders()) {
        this.loading = false;
        if (this.isFile) {
          this.importedFileErrors = 'Headers do not match for the table';
        } else {
          this.pastedDataError = 'Headers do not match for the current table';
        }
        return;
      }

      // Check that no RLS fields are present in the data
      var rlsColumnInData = this.hasRLSInData();
      if(rlsColumnInData){
        this.loading = false;
        if (this.isFile) {
          this.importedFileErrors = `Column '${rlsColumnInData}' is a Row Level Security Column and cannot be imported`;
        } else {
          this.pastedDataError = `Column '${rlsColumnInData}' is a Row Level Security Column and cannot be uploaded`;;
        }
        return;
      }

      // Check that no non-writable fields are present in the data
      const nonWritableFieldNames = this.getNonWritableFields().map(f => f.name);
      const readonlyColumnsInUpload = this.headers.filter(h => nonWritableFieldNames.includes(h));
      if (readonlyColumnsInUpload.length > 0) {
        this.loading = false;
        const message = `You do not have write permissions on the following field(s): '${readonlyColumnsInUpload.join(', ')}'.\nPlease remove these fields and try again.`;
        if (this.isFile) {
          this.importedFileErrors = message;
        } else {
          this.pastedDataError = message;
        }
        return;
      }

      lines.map((line, indexLine) => {
        line.length = this.headers.length;

        if (indexLine < 1 || line.every(i => !i)) return false;

        const obj = {};

        this.headers.map((header, indexHeader) => {
          obj[header] = line[indexHeader];
        });
        result.push(obj);
      });

      if (result.length === 0) {
        this.loading = false;
        if (this.isFile) {
          this.importedFileErrors = 'Unable to find data';
        } else {
          this.pastedDataError = 'Unable to find data';
        }
      } else {
        this.loading = false;
        this.showImportTableViewModal(result);
      }
    },
    uploadXlsx(e) {
      const file = e.target.files[0];
      if (file) {
        if (file.name.slice(file.name.length - 5).toLowerCase() !== '.xlsx') {
          this.isFile = false;
          this.importedFileErrors = 'File should be XLSX';
        } else {
          this.isFile = true;
          this.importedFileErrors = '';
          this.loading = true;
          readXlsxFile(file).then(async (rows) => {
            await this.parseFile(rows, true);
          });
        }
      }
    },
    hasRLSInData() {
      var column = undefined;
      this.fields.filter(f=>f.isRLSField).forEach(f => {
        this.headers = this.headers.map(h => h.toString().replace(new RegExp(String.fromCharCode(13), 'g'), ''));

        if (this.headers.includes(f.name)) {
          column = f.name;
        }
      });
      return column;
    },
    getNonWritableFields() {
      return this.fields.filter(f =>
        ['read', 'none', 'deny'].includes((f.currentUserPermissionLevel || '').toLowerCase()),
      );
    },
    isValidColumns() {
      const notMatchedColumns = [];
      const nonWriteableFields = this.getNonWritableFields();

      this.fields.filter(f=>!f.isRLSField && !nonWriteableFields.includes(f)).forEach(f => {
        this.headers = this.headers.map(h => h.toString().replace(new RegExp(String.fromCharCode(13), 'g'), ''));

        if (!this.headers.includes(f.name)) {
          notMatchedColumns.push(f.name);
        }
      });
      return notMatchedColumns;
    },
    isValidHeaders() {
      const nonWriteableFields = this.getNonWritableFields();

      return this.fields.filter(f=>!f.isRLSField && !nonWriteableFields.includes(f)).map(f => f.name).every(e => {
        // For files which are download on windows for some reason latest header contains enter in the name,
        // So we are removing enter characters from the header
        this.headers = this.headers.map(h => h.replace(new RegExp(String.fromCharCode(13), 'g'), ''));
        return this.headers.includes(e);
      });
    },
    onChange() {
      if (this.isValidColumns().length > 0 || !this.isValidHeaders()) {
        this.tableTextData = '';
        this.$refs.textArea.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form-control {
  opacity: 0;
}

.invalid-data {
  width: 100%;
  margin-top: 0.25rem;
  font-size: 80%;
  color: #dc3545;
}

.paste-data-text {
  height: 3em;
  position: relative;
}

.paste-data-text h5 {
  position: absolute;
  top: 100%;
  left: 50%;
  margin: 0 -50% 0 0;
  transform: translate(-50%, -50%);
  color: grey;
  padding: 17px;
}
</style>
